import _extends from "@babel/runtime/helpers/esm/extends";
// Final fallback when neither the locale-driven `roleDisplayNames` parameter
// (provided by `useMessageAuthor`) nor any other author signal is available.
// Used for non-React call sites (selectors, store helpers) that don't have a
// `ChatLocaleText` in scope.
const DEFAULT_ROLE_DISPLAY_NAMES = {
  user: 'User',
  assistant: 'Assistant',
  system: 'System'
};
function getRoleDisplayName(role, roleDisplayNames) {
  if (!role) {
    return undefined;
  }
  return roleDisplayNames?.[role] ?? DEFAULT_ROLE_DISPLAY_NAMES[role];
}
function normalizeOptionalString(value) {
  return value == null || value === '' ? undefined : value;
}
function resolveMessageAuthorId(message, parameters) {
  return normalizeOptionalString(parameters.getMessageAuthorId?.(message) ?? message.author?.id ?? undefined);
}
function resolveMatchedMember(authorId, parameters) {
  if (!authorId) {
    return undefined;
  }
  if (parameters.currentUser?.id === authorId) {
    return parameters.currentUser;
  }
  const member = parameters.members?.find(candidate => candidate.id === authorId);
  if (member) {
    return member;
  }
  return parameters.activeConversation?.participants?.find(candidate => candidate.id === authorId);
}
export function resolveMessageAuthor(message, parameters) {
  if (!message) {
    return null;
  }
  const id = resolveMessageAuthorId(message, parameters);
  const matchedMember = resolveMatchedMember(id, parameters);
  // Display-name fallback chain:
  //   1. consumer getter (`getMessageAuthorDisplayName`)
  //   2. per-message `author.displayName`
  //   3. matched member's `displayName`
  //   4. `currentUser.displayName` (only for `role: 'user'` messages that have
  //      no explicit author identity — this lets a `currentUser` configured at
  //      the ChatProvider level retroactively name outgoing messages that were
  //      sent without per-message author metadata)
  //   5. locale-driven role label (`messageAuthor{User,Assistant,System}Label`)
  const currentUserFallback = message.role === 'user' && id == null ? parameters.currentUser?.displayName : undefined;
  const displayName = normalizeOptionalString(parameters.getMessageAuthorDisplayName?.(message) ?? message.author?.displayName ?? matchedMember?.displayName ?? currentUserFallback) ?? getRoleDisplayName(message.role, parameters.roleDisplayNames);
  const avatarUrl = normalizeOptionalString(parameters.getMessageAuthorAvatarUrl?.(message) ?? message.author?.avatarUrl ?? matchedMember?.avatarUrl);
  if (id == null && displayName == null && avatarUrl == null) {
    return null;
  }

  // When `currentUser` is configured, the author's id determines ownership.
  // Otherwise fall back to the role so single-user-vs-assistant chats — where
  // messages typically carry no author info — keep the legacy alignment.
  const isOwnMessage = parameters.currentUser?.id != null ? id != null && parameters.currentUser.id === id : message.role === 'user';
  return {
    id,
    displayName,
    avatarUrl,
    isOwnMessage
  };
}
export function getMessageWithResolvedAuthor(message, parameters) {
  if (!message) {
    return null;
  }
  const resolvedAuthor = resolveMessageAuthor(message, parameters);
  if (!resolvedAuthor) {
    return message;
  }
  const nextAuthor = _extends({}, message.author ?? {}, resolvedAuthor.id != null ? {
    id: resolvedAuthor.id
  } : {}, resolvedAuthor.displayName != null ? {
    displayName: resolvedAuthor.displayName
  } : {}, resolvedAuthor.avatarUrl != null ? {
    avatarUrl: resolvedAuthor.avatarUrl
  } : {});
  const authorChanged = message.author?.id !== nextAuthor.id || message.author?.displayName !== nextAuthor.displayName || message.author?.avatarUrl !== nextAuthor.avatarUrl;
  if (!authorChanged) {
    return message;
  }
  return _extends({}, message, {
    author: nextAuthor
  });
}
export function getMessageAuthorGroupingKey(message, parameters) {
  if (!message) {
    return undefined;
  }
  return resolveMessageAuthor(message, parameters)?.id ?? message.role ?? undefined;
}